import { Link } from "react-router-dom";
import { ANIMALS, HERO_IMAGE } from "@/data/animals";
import { ArrowRight, Leaf, MapPinLine, Eye } from "@phosphor-icons/react";
import { Editable } from "@/components/Editable";

// Bento grid placement classes per index to create asymmetric, vintage feel.
const GRID_CLASSES = [
  // 0: wide (trout)
  "md:col-span-2 md:row-span-1",
  // 1: tall (mule deer)
  "md:col-span-1 md:row-span-2",
  // 2: square (coyote)
  "md:col-span-1 md:row-span-1",
  // 3: square (jackrabbit)
  "md:col-span-1 md:row-span-1",
  // 4: wide (black bear)
  "md:col-span-2 md:row-span-1",
  // 5: square (lizard)
  "md:col-span-1 md:row-span-1",
  // 6: tall (pronghorn)
  "md:col-span-1 md:row-span-2",
  // 7: square (hummingbird)
  "md:col-span-1 md:row-span-1",
];

const CARD_HEIGHTS = [
  "h-[340px]",
  "h-[700px]",
  "h-[340px]",
  "h-[340px]",
  "h-[340px]",
  "h-[340px]",
  "h-[700px]",
  "h-[340px]",
];

export default function Home() {
  const sasquatch = ANIMALS.find((a) => a.id === "sasquatch");
  const others = ANIMALS.filter((a) => a.id !== "sasquatch");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="El Porvenir landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F4F1EA]/40 via-[#F4F1EA]/20 to-[#F4F1EA]" />
          <div className="absolute inset-0 paper-grain opacity-40 mix-blend-multiply" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-28 pb-40 md:pt-40 md:pb-56">
          <div className="font-mono-label text-[11px] text-[#2A3A2C] fade-up">
            <Editable id="home.hero.kicker" defaultValue="Volume I · A Field Guide to the Wildlife of" />
          </div>
          <h1 className="font-serif-display text-6xl sm:text-7xl lg:text-[8rem] leading-[0.95] text-[#2A3A2C] mt-3 fade-up">
            El&nbsp;Porvenir,
            <br />
            <span className="italic font-medium">New Mexico</span>
          </h1>
          <div className="mt-8 max-w-xl fade-up">
            <p className="text-lg text-[#2E362F] leading-relaxed">
              <Editable
                id="home.hero.blurb"
                multiline
                defaultValue={"Nine creatures of the Sangre de Cristo foothills — from the crimson-slashed trout in the canyon streams to the cryptid in the pines. Read, watch, and log what you find."}
              />
            </p>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4 fade-up">
            <a
              href="#field-guide"
              data-testid="hero-explore-btn"
              className="inline-flex items-center gap-2 bg-[#2A3A2C] text-[#F4F1EA] px-7 py-4 font-mono-label text-[11px] hover:bg-[#1A251C] transition-colors"
            >
              Explore the Guide
              <ArrowRight size={16} />
            </a>
            <Link
              to="/spotter-log"
              data-testid="hero-log-btn"
              className="inline-flex items-center gap-2 border border-[#2A3A2C] text-[#2A3A2C] px-7 py-4 font-mono-label text-[11px] hover:bg-[#2A3A2C] hover:text-[#F4F1EA] transition-colors"
            >
              <Eye size={16} />
              Log a Sighting
            </Link>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative border-y border-[#D1C9B8] bg-[#FAF8F5]/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#D1C9B8]">
            {[
              { k: "Elevation", v: "7,290 ft" },
              { k: "Habitat", v: "Sangre de Cristos" },
              { k: "Species in Guide", v: "09" },
              { k: "First Edition", v: "MMXXV" },
            ].map((s, i) => (
              <div key={i} className="px-5 md:px-7 py-7">
                <div className="font-mono-label text-[10px] text-[#5C6B5E]">{s.k}</div>
                <div className="font-serif-display text-2xl text-[#2A3A2C] mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FIELD GUIDE SECTION HEADER */}
      <section id="field-guide" className="max-w-7xl mx-auto px-5 md:px-8 pt-24 md:pt-32">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="font-mono-label text-[11px] text-[#5C6B5E]">
              § Plates I–VIII · The Catalogue
            </div>
            <h2 className="font-serif-display text-5xl md:text-6xl text-[#2A3A2C] mt-3 leading-tight">
              <Editable
                id="home.catalogue.heading"
                multiline
                defaultValue={"The creatures you may cross paths with."}
              />
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-[#5C6B5E] leading-relaxed">
              <Editable
                id="home.catalogue.blurb"
                multiline
                defaultValue={"Each plate is an index card — common name, scientific name, and the small particularities that make a species itself. Tap a card to read the full profile and fun facts."}
              />
            </p>
          </div>
        </div>
        <div className="ink-rule mt-10" />
      </section>

      {/* BENTO GRID */}
      <section
        className="max-w-7xl mx-auto px-5 md:px-8 mt-10"
        data-testid="wildlife-grid"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5 auto-rows-min">
          {others.map((animal, i) => (
            <Link
              to={`/species/${animal.id}`}
              key={animal.id}
              data-testid={`animal-card-${animal.id}`}
              className={`field-card group relative block overflow-hidden ${
                GRID_CLASSES[i] || ""
              } ${CARD_HEIGHTS[i] || "h-[340px]"}`}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="absolute inset-0">
                <img
                  src={animal.image}
                  alt={animal.name}
                  className="photo w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
              </div>

              <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
                <span className="font-mono-label text-[10px] text-white/90 bg-black/30 px-2 py-1 backdrop-blur-sm">
                  Plate No. {animal.number}
                </span>
                <span className="font-mono-label text-[10px] text-white/90 bg-black/30 px-2 py-1 backdrop-blur-sm">
                  {animal.category.split(" · ")[0]}
                </span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-white">
                <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">
                  {animal.name}
                </h3>
                <p className="font-mono-label text-[10px] text-white/75 mt-1 italic normal-case tracking-normal">
                  {animal.scientific}
                </p>
                <div className="mt-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="font-mono-label text-[10px]">Read profile</span>
                  <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SASQUATCH — dark break */}
      <section
        className="relative mt-32 overflow-hidden"
        data-testid="sasquatch-section"
      >
        <div className="absolute inset-0">
          <img
            src={sasquatch.image}
            alt="Dark forest"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(26, 28, 25, 0.78)" }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-28 md:py-40">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-7">
              <div className="font-mono-label text-[11px] text-[#B75D3F]">
                Plate IX · Unverified
              </div>
              <h2 className="font-serif-display text-5xl md:text-7xl text-[#F4F1EA] mt-4 leading-[0.95]">
                <Editable
                  id="home.sasquatch.heading"
                  multiline
                  defaultValue={"Sasquatch: a note on the unverified."}
                />
              </h2>
              <p className="mt-8 text-[#D1C9B8] leading-relaxed max-w-xl text-lg">
                <Editable
                  id="home.sasquatch.blurb"
                  multiline
                  defaultValue={"Sasquatch, also called Bigfoot, is a large bipedal figure reported across forested North America for more than a century. No verified specimen, skeleton, or DNA evidence exists. It is included here because reports cluster in old-growth conifer country like the canyons above El Porvenir, and because indigenous oral traditions of the region long pre-date the modern \"Bigfoot\" era."}
                />
              </p>
              <Link
                to="/species/sasquatch"
                data-testid="sasquatch-cta"
                className="mt-10 inline-flex items-center gap-2 border border-[#F4F1EA]/60 text-[#F4F1EA] px-7 py-4 font-mono-label text-[11px] hover:bg-[#F4F1EA] hover:text-[#1A1C19] transition-colors"
              >
                Read the Full Entry
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="md:col-span-5 md:pt-10">
              <div className="border border-[#F4F1EA]/20 p-7 backdrop-blur-sm bg-black/30">
                <div className="font-mono-label text-[10px] text-[#B75D3F]">
                  Status — at a glance
                </div>
                <ul className="mt-4 space-y-3 text-[#F4F1EA] text-base leading-relaxed">
                  <li>
                    <span className="font-mono-label text-[10px] text-[#D1C9B8]/70 block">
                      Verified specimens
                    </span>
                    None on record.
                  </li>
                  <li>
                    <span className="font-mono-label text-[10px] text-[#D1C9B8]/70 block">
                      Most-studied evidence
                    </span>
                    1967 Patterson-Gimlin film (disputed).
                  </li>
                  <li>
                    <span className="font-mono-label text-[10px] text-[#D1C9B8]/70 block">
                      Common misidentification
                    </span>
                    Black bear standing on hind legs.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPOTTED LOG TEASER */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="font-mono-label text-[11px] text-[#5C6B5E]">
              § The Spotter Log
            </div>
            <h2 className="font-serif-display text-5xl md:text-6xl text-[#2A3A2C] mt-3 leading-tight">
              <Editable
                id="home.spotterlog.heading"
                multiline
                defaultValue={"What did you see?"}
              />
            </h2>
            <p className="mt-6 text-[#5C6B5E] leading-relaxed text-lg">
              <Editable
                id="home.spotterlog.blurb"
                multiline
                defaultValue={"Visitors and locals keep this logbook together. Add your sighting — common deer or strange shadow — and read what others have written down before you."}
              />
            </p>
            <Link
              to="/spotter-log"
              data-testid="spotted-log-cta"
              className="mt-8 inline-flex items-center gap-2 bg-[#B75D3F] text-white px-7 py-4 font-mono-label text-[11px] hover:bg-[#964B32] transition-colors"
            >
              Open the Logbook
              <ArrowRight size={16} />
            </Link>
          </div>
          <div className="md:col-span-6">
            <div className="bg-[#FAF8F5] border border-[#D1C9B8] p-8 deckled-edge">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[#2A3A2C]">
                  <Leaf size={16} weight="duotone" />
                  <span className="font-mono-label text-[10px]">Entry No. 0173</span>
                </div>
                <span className="font-mono-label text-[10px] text-[#5C6B5E]">
                  · Logbook
                </span>
              </div>
              <p className="font-serif-display text-3xl text-[#2A3A2C] mt-5 leading-snug italic">
                “Two mule deer at the bend below the campground — buck with a
                broken left antler.”
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-mono-label text-[10px] text-[#5C6B5E]">Where</div>
                  <div className="flex items-center gap-1 text-[#2A3A2C] mt-1">
                    <MapPinLine size={14} /> El Porvenir Canyon Road
                  </div>
                </div>
                <div>
                  <div className="font-mono-label text-[10px] text-[#5C6B5E]">Observer</div>
                  <div className="text-[#2A3A2C] mt-1">M. Aragón</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
